import { memo, useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type PointerEvent as ReactPointerEvent } from 'react'
import { Link, useParams } from 'react-router'
import { Armchair, BookOpen, CircleDot, Printer, Sofa, Coffee, Droplets, Footprints, Gamepad2, Leaf, ListTodo, MapPin, Maximize2, MessageCircle, Minimize2, Minus, Moon, Plus, Refrigerator, Send, type LucideIcon } from 'lucide-react'
import { PageHeader } from '@/components/composites/states'
import { UserAvatar } from '@/components/composites/user-avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import { useTick } from '@/features/worktime/api'
import { formatClock } from '@/features/worktime/clock'
import { type TeamPerson } from './api'
import {
  findPath,
  headingTo,
  nearestDesk,
  nearestPerson,
  step,
  unstick,
  zoneAt,
  type Pos,
  type PropKind,
  type World,
} from './arena'
import { recordOwn, useChatLines } from './chatLog'
import { ZONE_STYLE } from './Minimap'
import { CallTiles } from './CallTiles'
import { GamesButton, useGamesDialog } from '@/features/games'
import { MediaButtons } from './MediaButtons'
import { Minimap } from './Minimap'
import { useOfficeLive, usePosRef, type OfficeLive } from './OfficeLive'
import { PersonHeader, PersonStatus } from './PersonStatus'

/** Wall-clock time, outside the component so event handlers may stamp things with it. */
const stamp = () => Date.now()

const SPEED = 215 // pixels per second on the floor
const REMOTE_SPEED = 300 // how fast other people's avatars catch up to where they were last reported
const AVATAR = 44
const ZOOM_MIN = 0.4
const ZOOM_MAX = 1.6
const clampZoom = (z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100))
const EMOTES = ['👋', '🎉', '👍', '☕', '❤️', '😂']

const KEYS: Record<string, Pos> = {
  ArrowUp: { x: 0, y: -1 },
  KeyW: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
  KeyS: { x: 0, y: 1 },
  ArrowLeft: { x: -1, y: 0 },
  KeyA: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  KeyD: { x: 1, y: 0 },
}

const PROP: Record<PropKind, { cls: string; Icon?: LucideIcon }> = {
  plant: { cls: 'rounded-full border-success/40 bg-success-soft', Icon: Leaf },
  cooler: { cls: 'rounded-md border-info/40 bg-info-soft', Icon: Droplets },
  sofa: { cls: 'rounded-xl border-brand/30 bg-brand-soft', Icon: Armchair },
  table: { cls: 'rounded-lg border-border-strong bg-card' },
  counter: { cls: 'rounded-md border-border-strong bg-card', Icon: Coffee },
  fridge: { cls: 'rounded-md border-border-strong bg-card', Icon: Refrigerator },
  whiteboard: { cls: 'rounded-sm border-border bg-card' },
  arcade: { cls: 'rounded-lg border-brand-accent/40 bg-brand-accent-soft', Icon: Gamepad2 },
  bookshelf: { cls: 'rounded-sm border-warning/40 bg-warning-soft', Icon: BookOpen },
  printer: { cls: 'rounded-md border-border-strong bg-card', Icon: Printer },
  pingpong: { cls: 'rounded-lg border-info/40 bg-info-soft', Icon: CircleDot },
  beanbag: { cls: 'rounded-full border-brand/30 bg-brand-soft', Icon: Sofa },
}

interface Remote {
  x: number
  y: number
  tx: number
  ty: number
}

/**
 * The office: a floor plan with a pod of desks per team, two meeting rooms, a kitchen and a lounge
 * behind walls and doors. You walk it (arrow keys, WASD, or click anywhere and you path around the
 * furniture), and everyone else who has the page open walks it with you: positions, words said out
 * loud and emotes are shared, and the camera follows you.
 *
 * Only presence is shared — where you stand, and anything you choose to say. Nothing keeps a trail
 * of movement, and a person who closes the page leaves the floor within about twenty seconds. People
 * who are not on the page are at their desks (lit while they time an item) or away.
 */
export function OfficePage() {
  const { workspaceSlug = '' } = useParams()
  const live = useOfficeLive()

  return (
    <div className="mx-auto w-full max-w-none px-2 pb-6 md:px-4">
      <PageHeader
        kicker="Team"
        title="The office"
        description="Walk the floor with WASD or the arrow keys, or click where you want to go. Press Enter to say something. Turn on your mic or camera to talk to people near you."
        actions={
          <Link to={paths.teamToday(workspaceSlug)} className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-sm font-medium hover:bg-muted">
            <ListTodo className="size-4" /> Team list
          </Link>
        }
      />
      {live.isLoading || !live.world ? (
        <Skeleton className="h-[560px] w-full" />
      ) : live.isError || !live.data ? (
        <p className="text-sm text-danger">The office could not be loaded. Try again in a moment.</p>
      ) : (
        <OfficeFloor live={live} workspaceSlug={workspaceSlug} />
      )}
    </div>
  )
}

function OfficeFloor({ live, workspaceSlug }: { live: OfficeLive; workspaceSlug: string }) {
  const gamesRoom = useGamesDialog()
  const data = live.data!
  const world = live.world!
  const nav = live.nav!
  const people = data.people
  const meId = data.me
  const byId = useMemo(() => new Map(people.map((p) => [p.userId, p])), [people])
  const me = byId.get(meId)
  const now = useTick(people.some((p) => p.working))
  const chat = useChatLines()

  // The viewport is a state-held element: it only mounts once the feed has loaded.
  const [vpEl, setVpEl] = useState<HTMLDivElement | null>(null)
  const [vpSize, setVpSize] = useState({ w: 900, h: 560 })
  useEffect(() => {
    if (!vpEl) return
    const measure = () => setVpSize({ w: vpEl.clientWidth, h: vpEl.clientHeight })
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(vpEl)
    return () => ro.disconnect()
  }, [vpEl])
  // Zoom is yours to set (buttons, mouse wheel); a phone starts further out because the screen is small.
  const [userZoom, setUserZoom] = useState(0.85)
  const zoom = clampZoom(userZoom * (vpSize.w < 560 ? 0.8 : 1))
  const [fullscreen, setFullscreen] = useState(false)
  useEffect(() => {
    if (!vpEl) return
    const wheel = (e: WheelEvent) => {
      e.preventDefault()
      setUserZoom((z) => clampZoom(z * (e.deltaY < 0 ? 1.1 : 1 / 1.1)))
    }
    vpEl.addEventListener('wheel', wheel, { passive: false })
    const fs = () => setFullscreen(document.fullscreenElement === vpEl)
    document.addEventListener('fullscreenchange', fs)
    return () => {
      vpEl.removeEventListener('wheel', wheel)
      document.removeEventListener('fullscreenchange', fs)
    }
  }, [vpEl])
  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen()
    else void vpEl?.requestFullscreen?.()
  }

  // Everything that moves lives in refs: a frame must not re-render the office.
  const home = world.desks.find((d) => d.userId === meId)?.seat ?? world.spawn
  // Shared with the widget and the reporter, which are the other readers of where you stand.
  const posRef = usePosRef()
  useEffect(() => {
    if (!posRef.current) posRef.current = home
  }, [posRef, home])
  const pathRef = useRef<Pos[]>([])
  const held = useRef(new Set<string>())
  const worldRef = useRef(world)
  const navRef = useRef(nav)
  const zoomRef = useRef(zoom)
  const sizeRef = useRef(vpSize)
  const camRef = useRef({ x: 0, y: 0, ready: false })
  const layerRef = useRef<HTMLDivElement>(null)
  const meElRef = useRef<HTMLDivElement>(null)
  const remotes = useRef(new Map<string, Remote>())
  const remoteEls = useRef(new Map<string, HTMLDivElement>())
  const chatInput = useRef<HTMLInputElement>(null)

  const [nearId, setNearId] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [myZone, setMyZone] = useState(() => zoneAt(world, home))
  const [say, setSay] = useState<{ text: string; key: number } | null>(null)
  const [emote, setEmote] = useState<{ icon: string; key: number } | null>(null)
  const [marker, setMarker] = useState<{ x: number; y: number; key: number } | null>(null)
  const [draft, setDraft] = useState('')

  useEffect(() => {
    worldRef.current = world
    navRef.current = nav
    zoomRef.current = zoom
    sizeRef.current = vpSize
    // A floor that changed under you (someone joined) must not leave you inside a new desk.
    if (posRef.current) posRef.current = unstick(posRef.current, world)
    pathRef.current = []
  }, [world, nav, zoom, vpSize])

  // Other people: where the server last saw them is where their avatar walks to.
  useEffect(() => {
    const map = remotes.current
    const live = new Set<string>()
    for (const p of people) {
      if (p.userId === meId || !p.pos) continue
      live.add(p.userId)
      const r = map.get(p.userId)
      if (r) {
        r.tx = p.pos.x
        r.ty = p.pos.y
      } else map.set(p.userId, { x: p.pos.x, y: p.pos.y, tx: p.pos.x, ty: p.pos.y })
    }
    for (const id of [...map.keys()]) if (!live.has(id)) map.delete(id)
  }, [people, meId])

  useEffect(() => {
    const typing = (t: EventTarget | null) => t instanceof HTMLElement && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))
    const down = (e: KeyboardEvent) => {
      if (typing(e.target) || e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key === 'Enter') {
        e.preventDefault()
        chatInput.current?.focus()
        return
      }
      if (KEYS[e.code]) {
        e.preventDefault()
        pathRef.current = []
        held.current.add(e.code)
      }
    }
    const up = (e: KeyboardEvent) => held.current.delete(e.code)
    const blur = () => held.current.clear()
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    window.addEventListener('blur', blur)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
      window.removeEventListener('blur', blur)
    }
  }, [])

  // The game loop: your movement, everyone's tween, the camera.
  useEffect(() => {
    let raf = 0
    let last = performance.now()
    let nearPrev: string | null = null
    let zonePrev = ''
    const frame = (t: number) => {
      const dt = Math.min(0.05, (t - last) / 1000)
      last = t
      const w = worldRef.current
      if (!posRef.current) {
        raf = requestAnimationFrame(frame)
        return
      }
      let dx = 0
      let dy = 0
      for (const code of held.current) {
        const k = KEYS[code]
        if (k) {
          dx += k.x
          dy += k.y
        }
      }
      if (dx === 0 && dy === 0 && pathRef.current.length) {
        const h = headingTo(posRef.current!, pathRef.current[0]!)
        if (h) {
          dx = h.x
          dy = h.y
        } else pathRef.current.shift()
      }
      let moving = false
      if (dx !== 0 || dy !== 0) {
        const len = Math.hypot(dx, dy)
        const before = posRef.current!
        const moved = step(before, (dx / len) * SPEED * dt, (dy / len) * SPEED * dt, w)
        if (moved.x === before.x && moved.y === before.y) pathRef.current = []
        else moving = true
        posRef.current = moved
      }
      const p = posRef.current!
      const bob = (el: HTMLElement | null, on: boolean, phase: number) => {
        const inner = el?.firstElementChild as HTMLElement | null
        if (inner) inner.style.transform = on ? `translateY(${(-Math.abs(Math.sin(t / 110 + phase)) * 3).toFixed(1)}px)` : ''
      }
      const meEl = meElRef.current
      if (meEl) {
        meEl.style.transform = `translate(${p.x - AVATAR / 2}px, ${p.y - AVATAR / 2}px)`
        meEl.style.opacity = '1'
        bob(meEl, moving, 0)
      }

      const others: Array<{ userId: string; x: number; y: number }> = []
      let i = 0
      for (const [id, r] of remotes.current) {
        const ddx = r.tx - r.x
        const ddy = r.ty - r.y
        const dist = Math.hypot(ddx, ddy)
        if (dist > 700) {
          r.x = r.tx
          r.y = r.ty
        } else if (dist > 0.5) {
          const f = Math.min(1, (REMOTE_SPEED * dt) / dist)
          r.x += ddx * f
          r.y += ddy * f
        }
        const el = remoteEls.current.get(id)
        if (el) {
          el.style.transform = `translate(${r.x - AVATAR / 2}px, ${r.y - AVATAR / 2}px)`
          el.style.opacity = '1'
          bob(el, dist > 3, i)
        }
        others.push({ userId: id, x: r.x, y: r.y })
        i++
      }

      // Camera: follow, ease, and never show past the walls.
      const z = zoomRef.current
      const { w: vw, h: vh } = sizeRef.current
      const aim = (focus: number, view: number, total: number) => {
        const span = total * z
        return span <= view ? (span - view) / 2 : Math.min(span - view, Math.max(0, focus * z - view / 2))
      }
      const tx = aim(p.x, vw, w.w)
      const ty = aim(p.y, vh, w.h)
      const cam = camRef.current
      if (!cam.ready) {
        cam.x = tx
        cam.y = ty
        cam.ready = true
      } else {
        const k = Math.min(1, dt * 7)
        cam.x += (tx - cam.x) * k
        cam.y += (ty - cam.y) * k
      }
      const layer = layerRef.current
      if (layer) layer.style.transform = `translate(${-cam.x}px, ${-cam.y}px) scale(${z})`

      const near = nearestPerson(p, others)?.userId ?? nearestDesk(p, w)?.userId ?? null
      if (near !== nearPrev) {
        nearPrev = near
        setNearId(near)
      }
      const zone = zoneAt(w, p)
      if (zone.id !== zonePrev) {
        zonePrev = zone.id
        setMyZone(zone)
      }
      raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)
    return () => cancelAnimationFrame(raf)
  }, [])

  const walkTo = useCallback((target: Pos) => {
    pathRef.current = findPath(navRef.current, posRef.current ?? worldRef.current.spawn, target) ?? []
  }, [])

  const onFloorPointer = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect()
      const target = { x: (e.clientX - rect.left + camRef.current.x) / zoom, y: (e.clientY - rect.top + camRef.current.y) / zoom }
      walkTo(target)
      setSelectedId(null)
      setMarker({ ...target, key: stamp() })
      window.setTimeout(() => setMarker((m) => (m && stamp() - m.key >= 650 ? null : m)), 700)
    },
    [walkTo, zoom],
  )

  const sendSay = (e: FormEvent) => {
    e.preventDefault()
    const text = draft.trim().slice(0, 80)
    setDraft('')
    chatInput.current?.blur()
    if (!text) return
    const key = stamp()
    setSay({ text, key })
    recordOwn(meId, me?.displayName ?? 'You', text)
    live.announce({ say: text })
    window.setTimeout(() => setSay((s) => (s && s.key === key ? null : s)), 8_000)
  }
  const sendEmote = (icon: string) => {
    const key = stamp()
    setEmote({ icon, key })
    live.announce({ emote: icon })
    window.setTimeout(() => setEmote((m) => (m && m.key === key ? null : m)), 3_000)
  }

  const focusId = selectedId ?? nearId
  const focus = focusId ? byId.get(focusId) : undefined
  const roaming = people.filter((p) => p.pos && p.userId !== meId)
  const seated = people.filter((p) => !p.pos && p.working && p.userId !== meId)
  const away = people.filter((p) => !p.pos && !p.working && p.userId !== meId)
  const zoneCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const p of people) if (p.pos && p.userId !== meId) {
      const z = zoneAt(world, p.pos).id
      counts.set(z, (counts.get(z) ?? 0) + 1)
    }
    return counts
  }, [people, meId, world])
  const hereCount = (zoneCounts.get(myZone.id) ?? 0) + 1
  const onFloor = roaming.length + 1

  return (
    <div className="grid gap-3 lg:grid-cols-[1fr_300px]">
      <div className="min-w-0">
        <div
          ref={setVpEl}
          className="relative h-[60vh] min-h-[420px] touch-none select-none overflow-hidden rounded-xl border border-border bg-surface-sunken lg:h-[calc(100vh-18.5rem)]"
          onPointerDown={onFloorPointer}
          role="application"
          aria-label="Office floor. Use WASD or the arrow keys to walk, or click where you want to go. Press Enter to chat."
        >
          <div ref={layerRef} className="absolute left-0 top-0 origin-top-left will-change-transform" style={{ width: world.w, height: world.h }}>
            <Scene world={world} byId={byId} meId={meId} nearId={nearId} now={now} onGames={live.writes ? gamesRoom.openLobby : null} />
            {seated.map((p) => {
              const desk = world.desks.find((d) => d.userId === p.userId)
              if (!desk) return null
              return (
                <div key={p.userId} className="pointer-events-auto absolute z-10 cursor-pointer" style={{ left: desk.seat.x - AVATAR / 2, top: desk.seat.y - AVATAR / 2, width: AVATAR, height: AVATAR }} onPointerDown={(e) => { e.stopPropagation(); setSelectedId(p.userId); walkTo(desk.seat) }}>
                  <UserAvatar person={{ id: p.userId, displayName: p.displayName, photoUrl: p.photoUrl }} size="md" className="size-11 ring-2 ring-success" />
                </div>
              )
            })}
            {roaming.map((p) => (
              <Walker
                key={p.userId}
                person={p}
                selected={focusId === p.userId}
                refCb={(el) => {
                  if (el) remoteEls.current.set(p.userId, el)
                  else remoteEls.current.delete(p.userId)
                }}
                onPick={() => {
                  setSelectedId(p.userId)
                  const r = remotes.current.get(p.userId)
                  if (r) walkTo({ x: r.x, y: r.y + 44 })
                }}
              />
            ))}
            <Walker
              person={me ?? ({ userId: meId, displayName: 'You', photoUrl: null, working: false } as TeamPerson)}
              isMe
              say={say?.text ?? null}
              sayKey={say?.key}
              emote={emote?.icon ?? null}
              emoteKey={emote?.key}
              refCb={(el) => {
                meElRef.current = el
              }}
            />
            {marker ? <span key={marker.key} className="pointer-events-none absolute z-10 size-5 -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full border-2 border-brand motion-reduce:animate-none" style={{ left: marker.x, top: marker.y }} /> : null}
          </div>

          <div className="pointer-events-none absolute left-3 top-3 flex flex-wrap items-center gap-1.5">
            <span className="flex items-center gap-1.5 rounded-full border border-border bg-card/95 px-3 py-1 text-xs font-semibold shadow-sm">
              <MapPin className="size-3.5 text-brand" /> {myZone.name}
              {hereCount > 1 ? <span className="font-normal text-text-muted">· {hereCount} here</span> : null}
            </span>
          </div>
          <div className="absolute right-3 top-3 flex items-center gap-1.5" onPointerDown={(e) => e.stopPropagation()}>
            <span className="pointer-events-none rounded-full border border-border bg-card/95 px-3 py-1 text-xs font-medium text-text-secondary shadow-sm">
              {onFloor} on the floor{seated.length ? ` · ${seated.length} at desks` : ''}
            </span>
            {live.writes ? <GamesButton me={meId} className="bg-card/95 shadow-sm" /> : null}
            <div className="flex items-center rounded-full border border-border bg-card/95 shadow-sm">
              <Button type="button" size="icon" variant="ghost" className="size-8 rounded-full" aria-label="Zoom out" onClick={() => setUserZoom((z) => clampZoom(z / 1.2))}>
                <Minus className="size-4" />
              </Button>
              <span className="num w-10 text-center text-xs tabular-nums text-text-secondary">{Math.round(zoom * 100)}%</span>
              <Button type="button" size="icon" variant="ghost" className="size-8 rounded-full" aria-label="Zoom in" onClick={() => setUserZoom((z) => clampZoom(z * 1.2))}>
                <Plus className="size-4" />
              </Button>
              <Button type="button" size="icon" variant="ghost" className="size-8 rounded-full" aria-label={fullscreen ? 'Exit full screen' : 'Full screen'} onClick={toggleFullscreen}>
                {fullscreen ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
              </Button>
            </div>
          </div>
          <Minimap world={world} roaming={roaming} posRef={posRef} className="pointer-events-none absolute bottom-16 right-3 hidden w-44 sm:block" />
          <div className="absolute left-3 top-14" onPointerDown={(e) => e.stopPropagation()}>
            <CallTiles calls={live.calls} people={people} local={live.cam ? live.localStream : null} meName={me?.displayName ?? 'You'} />
          </div>

          <div className="absolute inset-x-0 bottom-0 flex flex-wrap items-center justify-center gap-2 p-3" onPointerDown={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-0.5 rounded-full border border-border bg-card/95 p-0.5 shadow-sm">
              {EMOTES.map((icon) => (
                <button key={icon} type="button" className="grid size-8 place-items-center rounded-full text-lg transition-transform hover:scale-125 hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring" onClick={() => sendEmote(icon)} aria-label={`Send ${icon}`}>
                  {icon}
                </button>
              ))}
            </div>
            <div className="rounded-full border border-border bg-card/95 p-0.5 shadow-sm">
              <MediaButtons />
            </div>
            <form onSubmit={sendSay} className="flex min-w-0 items-center gap-1 rounded-full border border-border bg-card/95 p-0.5 pl-3 shadow-sm">
              <Input
                ref={chatInput}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Escape' && e.currentTarget.blur()}
                maxLength={80}
                placeholder="Say something… (Enter)"
                aria-label="Say something to the office"
                className="h-8 w-44 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 sm:w-56"
              />
              <Button type="submit" size="sm" variant="ghost" className="size-8 rounded-full p-0" aria-label="Send" disabled={!draft.trim()}>
                <Send className="size-4" />
              </Button>
            </form>
          </div>
        </div>
        <p className="mt-2 flex items-center gap-1.5 text-xs text-text-muted">
          <Footprints className="size-3.5" /> WASD or arrows to walk · click to go anywhere · Enter to talk · others see your spot for about 20 seconds after you leave this page.
        </p>
      </div>

      <aside className="grid content-start gap-3">
        <section className="rounded-lg border border-border bg-card p-3" aria-label="Details">
          {focus ? (
            <div className="grid gap-2">
              <PersonHeader person={focus} />
              {focus.team ? <p className="-mt-1 text-xs text-text-muted">{focus.team}</p> : null}
              <PersonStatus person={focus} fetchedAt={data.fetchedAt} now={now} />
              {focus.pos ? (
                <p className="text-xs text-text-muted">On the floor, in {zoneAt(world, focus.pos).name}.</p>
              ) : focus.working ? (
                <p className="text-xs text-text-muted">At their desk.</p>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-text-muted">Walk up to a desk or a person, or pick someone below, to see what they are working on.</p>
          )}
        </section>

        <section className="rounded-lg border border-border bg-card p-3" aria-label="Who is here">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">On the floor ({onFloor})</h2>
          <ul className="grid gap-1">
            <PersonRow person={me} isMe zone={myZone.name} onPick={() => setSelectedId(meId)} />
            {roaming.map((p) => (
              <PersonRow key={p.userId} person={p} zone={zoneAt(world, p.pos!).name} onPick={() => { setSelectedId(p.userId); const r = remotes.current.get(p.userId); if (r) walkTo({ x: r.x, y: r.y + 44 }) }} />
            ))}
          </ul>
          {seated.length ? (
            <>
              <h2 className="mb-1.5 mt-3 text-xs font-semibold uppercase tracking-wide text-text-muted">At their desks ({seated.length})</h2>
              <ul className="grid gap-1">
                {seated.map((p) => {
                  const desk = world.desks.find((d) => d.userId === p.userId)
                  return <PersonRow key={p.userId} person={p} zone={p.team ?? 'Desk'} onPick={() => { setSelectedId(p.userId); if (desk) walkTo(desk.seat) }} />
                })}
              </ul>
            </>
          ) : null}
          {away.length ? (
            <p className="mt-3 flex items-center gap-1.5 text-xs text-text-muted">
              <Moon className="size-3.5" /> {away.length} away
            </p>
          ) : null}
        </section>

        <section className="rounded-lg border border-border bg-card p-3" aria-label="What was said">
          <h2 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted">
            <MessageCircle className="size-3.5" /> Said on the floor
          </h2>
          {chat.length === 0 ? (
            <p className="text-sm text-text-muted">Nothing yet. Press Enter and say hello.</p>
          ) : (
            <ul className="grid max-h-40 gap-1 overflow-y-auto text-sm">
              {chat.slice(-8).map((l) => (
                <li key={l.id} className="leading-snug">
                  <span className="font-semibold">{l.userId === meId ? 'You' : l.name.split(' ')[0]}</span> <span className="text-text-secondary">{l.text}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <Link to={paths.teamToday(workspaceSlug)} className="sr-only">Team list</Link>
      </aside>
    </div>
  )
}

function PersonRow({ person, zone, isMe = false, onPick }: { person: TeamPerson | undefined; zone: string; isMe?: boolean; onPick: () => void }) {
  if (!person) return null
  return (
    <li>
      <button type="button" onClick={onPick} className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring">
        <UserAvatar person={{ id: person.userId, displayName: person.displayName, photoUrl: person.photoUrl }} size="sm" className={cn('size-6', person.working && 'ring-2 ring-success')} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{isMe ? 'You' : person.displayName}</span>
          <span className="block truncate text-xs text-text-muted">
            {zone}
            {person.working ? ` · ${person.card ? (person.card.code ?? 'working') : 'working'}` : ''}
          </span>
        </span>
      </button>
    </li>
  )
}

/** Someone standing on the floor. The game loop moves it; React only draws what it says. */
function Walker({
  person,
  isMe = false,
  selected = false,
  say = null,
  sayKey,
  emote = null,
  emoteKey,
  refCb,
  onPick,
}: {
  person: TeamPerson
  isMe?: boolean
  selected?: boolean
  say?: string | null
  sayKey?: number
  emote?: string | null
  emoteKey?: number
  refCb: (el: HTMLDivElement | null) => void
  onPick?: () => void
}) {
  const speech = isMe ? say : (person.pos?.say ?? null)
  const icon = isMe ? emote : (person.pos?.emote ?? null)
  const iconKey = isMe ? emoteKey : person.pos?.emoteAt
  return (
    <div
      ref={refCb}
      className={cn('absolute left-0 top-0 opacity-0', isMe ? 'pointer-events-none z-30' : 'pointer-events-auto z-20 cursor-pointer')}
      style={{ width: AVATAR, height: AVATAR }}
      onPointerDown={
        onPick
          ? (e) => {
              e.stopPropagation()
              onPick()
            }
          : undefined
      }
    >
      <div className="relative size-full">
        <UserAvatar
          person={{ id: person.userId, displayName: person.displayName, photoUrl: person.photoUrl }}
          size="md"
          className={cn('size-11 shadow-md', isMe ? 'ring-2 ring-brand' : person.working ? 'ring-2 ring-success' : 'ring-2 ring-card', selected && 'ring-brand-accent')}
        />
        <span className={cn('absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded px-1.5 text-micro font-semibold', isMe ? 'bg-brand text-text-inverse' : 'bg-card/95 text-text-secondary')}>
          {isMe ? 'You' : person.displayName.split(' ')[0]}
        </span>
        {speech ? (
          <span key={sayKey ?? person.pos?.sayAt} className="absolute bottom-full left-1/2 mb-1.5 w-max max-w-[190px] -translate-x-1/2 rounded-xl border border-border bg-card px-2.5 py-1 text-xs font-medium leading-snug shadow-md">
            {speech}
          </span>
        ) : null}
        {icon ? (
          <span key={iconKey} className="absolute -top-9 left-1/2 -translate-x-1/2 animate-bounce text-2xl motion-reduce:animate-none">
            {icon}
          </span>
        ) : null}
      </div>
    </div>
  )
}

/** The building itself. It changes only when someone starts or stops working, not every frame. */
const Scene = memo(function Scene({ world, byId, meId, nearId, now, onGames }: { world: World; byId: Map<string, TeamPerson>; meId: string; nearId: string | null; now: number; onGames: (() => void) | null }) {
  return (
    <>
      <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(to right, var(--color-border-soft) 1px, transparent 1px), linear-gradient(to bottom, var(--color-border-soft) 1px, transparent 1px)', backgroundSize: '48px 48px', opacity: 0.55 }} aria-hidden />
      {world.zones.map((z) => (
        <div key={z.id} className={cn('absolute rounded-xl border', ZONE_STYLE[z.kind])} style={{ left: z.x, top: z.y, width: z.w, height: z.h }}>
          {z.kind !== 'hall' ? (
            <p className={cn('absolute left-3 max-w-[90%] truncate font-semibold', z.kind === 'meeting' ? 'bottom-3' : 'top-2', z.kind === 'pod' ? 'text-sm text-text-secondary' : 'text-xs uppercase tracking-wide text-text-muted')}>{z.name}</p>
          ) : (
            <p className="absolute inset-x-0 bottom-1 text-center text-micro uppercase tracking-widest text-text-muted">Entrance</p>
          )}
        </div>
      ))}
      {world.walls.map((r, i) => (
        <div key={i} className="absolute rounded-sm bg-foreground/40" style={{ left: r.x, top: r.y, width: r.w, height: r.h }} />
      ))}
      {world.props.map((p, i) => {
        const spec = PROP[p.kind]
        // The arcade machine and the ping-pong table are where the games are.
        if (onGames && (p.kind === 'arcade' || p.kind === 'pingpong')) {
          return (
            <button
              key={i}
              type="button"
              className={cn('absolute grid cursor-pointer place-items-center border text-brand-accent-dark transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-ring', spec.cls)}
              style={{ left: p.x, top: p.y, width: p.w, height: p.h }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={onGames}
              aria-label="Open the games room"
              title="Play something: open the games room"
            >
              {spec.Icon ? <spec.Icon className="size-5" /> : null}
              <span className="pointer-events-none absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap text-micro font-semibold uppercase tracking-wide text-brand-accent-dark">Games</span>
            </button>
          )
        }
        return (
          <div key={i} className={cn('absolute grid place-items-center border text-text-muted', spec.cls)} style={{ left: p.x, top: p.y, width: p.w, height: p.h }}>
            {spec.Icon ? <spec.Icon className="size-4" /> : null}
          </div>
        )
      })}
      {world.desks.map((desk) => {
        const person = byId.get(desk.userId)
        if (!person) return null
        const since = person.startedAt ? Math.max(0, now - Date.parse(person.startedAt)) : 0
        return (
          <div key={desk.userId}>
            <div className={cn('absolute rounded-lg border bg-card shadow-sm', desk.userId === nearId ? 'border-brand' : person.working ? 'border-success/60' : 'border-border')} style={{ left: desk.x, top: desk.y, width: desk.w, height: desk.h }}>
              <div className={cn('absolute left-1/2 top-1.5 h-6 w-12 -translate-x-1/2 rounded-sm border', person.working ? 'border-success/60 bg-success/30' : 'border-border bg-muted')} aria-hidden />
              {person.working ? (
                <p className="absolute inset-x-1 bottom-0.5 truncate text-center text-micro font-medium text-text-secondary">
                  {person.card ? (person.card.code ?? person.card.title) : 'Private'} · <span className="num tabular-nums">{formatClock(since)}</span>
                </p>
              ) : null}
            </div>
            <p className={cn('absolute truncate text-center text-micro font-medium', person.working ? 'text-text-secondary' : 'text-text-muted')} style={{ left: desk.x - 5, top: desk.y + desk.h + 4, width: desk.w + 10 }}>
              {desk.userId === meId ? 'You' : person.displayName.split(' ')[0]}
            </p>
            {!person.pos && !person.working ? <span className="absolute size-3 rounded-full border border-border bg-muted" style={{ left: desk.seat.x - 6, top: desk.seat.y - 2 }} aria-hidden /> : null}
          </div>
        )
      })}
    </>
  )
})
